import { useEffect, useState } from 'react'
import { User } from 'lucide-react'
import { usersAPI } from '../lib/api'

export default function UserAvatar({
  user,
  sizeClass = 'h-10 w-10',
  iconClass = 'h-5 w-5',
  className = '',
  imageClassName = '',
}) {
  const [photoUrl, setPhotoUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const [refreshVersion, setRefreshVersion] = useState(0)

  useEffect(() => {
    const handlePhotoUpdate = (event) => {
      if (String(event.detail?.userId) === String(user?.id)) {
        setRefreshVersion((current) => current + 1)
      }
    }
    window.addEventListener('userProfilePhotoUpdated', handlePhotoUpdate)
    return () => window.removeEventListener('userProfilePhotoUpdated', handlePhotoUpdate)
  }, [user?.id])

  useEffect(() => {
    let active = true
    let objectUrl
    setFailed(false)
    setPhotoUrl(null)

    if (!user?.id) return () => {}

    // Avoid a guaranteed 404 for users that are known not to have a photo.
    // A photo update event increments refreshVersion, allowing a fresh fetch even
    // when the auth object has not been refreshed yet.
    if (!user.has_profile_photo && refreshVersion === 0) return () => {}

    const photoVersion = user.profile_photo_updated_at || (refreshVersion ? `refresh-${refreshVersion}` : undefined)
    usersAPI.fetchProfilePhoto(user.id, photoVersion)
      .then((response) => {
        if (!active) return
        objectUrl = URL.createObjectURL(response.data)
        setPhotoUrl(objectUrl)
      })
      .catch(() => {
        if (active) setFailed(true)
      })

    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [user?.id, user?.has_profile_photo, user?.profile_photo_updated_at, refreshVersion])

  const baseClass = `${sizeClass} shrink-0 overflow-hidden rounded-full ${className}`
  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt={`${user?.name || 'User'} profile`}
        className={`${baseClass} object-cover ${imageClassName}`}
      />
    )
  }

  const initial = user?.name?.trim()?.charAt(0)?.toUpperCase()
  return (
    <span className={`${baseClass} flex items-center justify-center bg-slate-800 font-semibold text-white`} aria-hidden>
      {initial || <User className={iconClass} />}
    </span>
  )
}
